import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { VisionBadge } from "@/components/ContactCtas";
import { ArrowRight, ArrowUpRight, VideoCamera, Brain, UserCheck, ShieldCheck } from "@phosphor-icons/react";
import { PRODUCTS, PILOT_URL, CONTACT_URL, HUB_PRICING_URL, slug } from "@/lib/brand";

const HERO_VIDEO = "https://customer-assets.emergentagent.com/jobs/83287065-a81d-4252-b3dd-18a513204039/videos/c511b95ca6feb795.mp4";
const HERO_IMG = "https://images.unsplash.com/photo-1626885930974-4b69aa21bbf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzYWZldHklMjBvZmZpY2VyfGVufDB8fHx8MTc4MTQ0NjQyOHww&ixlib=rb-4.1.0&q=85";
const WORKS_WITH = ["HazardLoop", "WorkSafeStory", "WSafe AI"].map((n) => PRODUCTS.find((p) => p.name === n));
const READINESS_LABEL = "$2,500 AI readiness assessment (credited toward a paid pilot)";
const PARTNERS = ["Genetec: integration in development", "Axis TIPP member", "Procore: coming soon"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Hero />
      <HowItWorks />
      <WhatItLooksAt />
      <FindingToFix />
      <Privacy />
      <WorksWith />
      <Partners />
      <Pricing />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Eyebrow({ children, dark }) {
  return <div className={`font-mono text-[10px] uppercase tracking-[0.3em] mb-3 ${dark ? "text-safety" : "text-ink-500"}`}>{children}</div>;
}


function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-ink-100">
      <div className="absolute inset-0 sf-grid-bg opacity-60 pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 flex flex-col justify-center">
          <VisionBadge />
          <h1 data-testid="hero-heading" className="mt-8 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-ink-900">
            Stop repetitive{" "}
            <span className="relative inline-block">
              <span className="relative z-10">strain injuries</span>
              <span className="hidden sm:block absolute left-0 right-0 bottom-2 h-3 bg-safety/30 -z-0" />
            </span>{" "}
            before they start.
          </h1>
          <p data-testid="hero-sub" className="mt-8 max-w-xl text-lg text-ink-700 leading-relaxed">
            Record a task on a phone or camera. SafeForm analyzes posture and motion, flags ergonomic risk, and shows a safer technique.
          </p>
          <div className="mt-10"><PilotButton testId="hero-cta-pilot" /></div>
        </div>
        <div className="lg:col-span-5">
          <div className="relative border border-ink-900 bg-ink-900 sf-scanline">
            <video ref={(el) => { if (el) { el.defaultMuted = true; el.muted = true; el.setAttribute("muted", ""); } }}
              src={HERO_VIDEO} poster={HERO_IMG} autoPlay muted loop playsInline preload="metadata"
              aria-label="SafeForm ergonomic analysis video" data-testid="hero-video"
              className="block w-full aspect-video lg:aspect-auto lg:h-[440px] object-cover opacity-95 bg-ink-900" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2">
              {["Posture", "Motion", "Technique"].map((l) => (
                <div key={l} className="bg-white/95 backdrop-blur-md p-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-900">{l}</div>
              ))}
            </div>
          </div>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">Findings are suggestions reviewed by a person.</div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", Icon: VideoCamera, t: "Record", d: "Record the task on a phone or a fixed camera." },
    { n: "02", Icon: Brain, t: "Analyze", d: "SafeForm analyzes posture and motion and flags ergonomic risk." },
    { n: "03", Icon: UserCheck, t: "Safer technique", d: "SafeForm suggests a safer technique. A person reviews it before it reaches the floor." },
  ];
  return (
    <section id="how-it-works" className="border-b border-ink-100 bg-ink-50" data-testid="section-how-it-works">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-ink-900 mb-12">Record. Analyze. Safer technique.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 border border-ink-100 bg-white">
          {steps.map(({ n, Icon, t, d }, i) => (
            <div key={n} className={`p-10 group hover:bg-ink-50 transition-colors ${i === 1 ? "md:border-x border-ink-100" : ""}`}>
              <div className="flex items-center justify-between mb-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{n}</div>
                <Icon size={28} weight="bold" className="text-ink-900 group-hover:text-safety transition-colors" />
              </div>
              <div className="font-display font-bold text-2xl tracking-tight text-ink-900">{t}</div>
              <div className="mt-3 text-ink-700 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatItLooksAt() {
  const items = [
    { t: "Lifting", d: "How loads are picked up, carried and set down." },
    { t: "Reaching", d: "Overhead and extended reaches away from the body." },
    { t: "Twisting", d: "Trunk rotation while handling or moving loads." },
    { t: "Repetition", d: "The same motion performed again and again across a task." },
  ];
  return (
    <section id="what-it-looks-at" className="border-b border-ink-100" data-testid="section-what-it-looks-at">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <Eyebrow>What it looks at</Eyebrow>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-ink-900 mb-12 max-w-3xl">Lifting, reaching, twisting and repetition.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <div key={it.t} data-testid={`looks-at-${slug(it.t)}`} className="p-8 border border-ink-100 border-l-2 border-l-safety sf-card bg-white">
              <div className="font-mono text-[10px] tracking-[0.3em] text-ink-500">0{i + 1}</div>
              <div className="mt-4 font-display font-bold text-2xl tracking-tight text-ink-900">{it.t}</div>
              <div className="mt-2 text-ink-700 leading-relaxed">{it.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FindingToFix() {
  const blocks = [
    { id: "finding-to-fix", eyebrow: "From finding to fix", t: "Findings become corrective actions in HazardLoop.", d: "Each reviewed finding can be sent to HazardLoop as a corrective action, so it is assigned, tracked and closed out.", link: PRODUCTS[1] },
    { id: "training", eyebrow: "Training", t: "Findings feed WorkSafeStory toolbox content.", d: "Reviewed findings and safer techniques feed WorkSafeStory, so toolbox content reflects the tasks your crews actually do.", link: PRODUCTS[5] },
  ];
  return (
    <section className="border-b border-ink-100 bg-ink-900 text-white">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10">
        {blocks.map((b) => (
          <div key={b.id} id={b.id} data-testid={`section-${b.id}`} className="bg-ink-900 p-10 lg:p-14">
            <Eyebrow dark>{b.eyebrow}</Eyebrow>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-[1.05]">{b.t}</h2>
            <p className="mt-6 text-white/70 leading-relaxed text-lg">{b.d}</p>
            <a href={b.link.url} data-testid={`${b.id}-link`} className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-safety hover:text-white transition-colors">
              Go to {b.link.name} <ArrowUpRight size={14} weight="bold" />
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

function Privacy() {
  return (
    <section id="privacy" className="border-b border-ink-100" data-testid="section-privacy">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Eyebrow>Privacy</Eyebrow>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] text-ink-900">The analysis focuses on the task.</h2>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { t: "The task, not the person", d: "SafeForm looks at posture and motion during the task. It is not used to judge or identify individual workers." },
            { t: "Face blur by default", d: "Faces in uploaded clips can be blurred before the video is stored or analyzed. The option is on by default for every upload." },
          ].map((p) => (
            <div key={p.t} className="p-8 border border-ink-100 bg-ink-50">
              <ShieldCheck size={24} weight="bold" className="text-safety" />
              <div className="mt-4 font-display font-bold text-xl tracking-tight text-ink-900">{p.t}</div>
              <div className="mt-2 text-ink-700 leading-relaxed">{p.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorksWith() {
  return (
    <section id="works-with" className="border-b border-ink-100 bg-ink-50" data-testid="section-works-with">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <Eyebrow>Works with</Eyebrow>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-ink-900 mb-12">Works with HazardLoop, WorkSafeStory and WSafe AI.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {WORKS_WITH.map((p) => (
            <a key={p.name} href={p.url} data-testid={`works-with-${slug(p.name)}`}
              className="group p-8 border border-ink-100 bg-white hover:border-ink-900 transition-colors">
              <div className="flex items-center justify-between">
                <div className="font-display font-black text-2xl tracking-tight text-ink-900">{p.name}</div>
                <ArrowUpRight size={20} weight="bold" className="text-ink-500 group-hover:text-safety transition-colors" />
              </div>
              <div className="mt-2 text-ink-700 leading-relaxed">{p.desc}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section id="partners" className="border-b border-ink-100" data-testid="section-partners">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
        <Eyebrow>Partners</Eyebrow>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-100 border border-ink-100">
          {PARTNERS.map((p) => (
            <div key={p} data-testid={`partner-${slug(p.split(":")[0])}`} className="bg-white p-8 font-display font-bold text-lg text-ink-900">{p}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="border-b border-ink-100 bg-ink-50" data-testid="section-pricing">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
        <p className="text-lg text-ink-900" data-testid="pricing-line">
          SafeForm is included in the Vision tier.{" "}
          <a href={HUB_PRICING_URL} data-testid="pricing-see-link" className="font-semibold underline underline-offset-4 decoration-safety hover:text-safety">See pricing</a>
        </p>
      </div>
    </section>
  );
}

function PilotButton({ testId }) {
  return (
    <a href={PILOT_URL}>
      <Button data-testid={testId} size="lg" className="rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-6 text-sm font-semibold">
        Book a pilot <ArrowRight size={16} className="ml-2" weight="bold" />
      </Button>
    </a>
  );
}

function FinalCta() {
  return (
    <section className="border-b border-ink-100 bg-ink-900 text-white" data-testid="section-final-cta">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] max-w-4xl">
          Stop repetitive strain injuries <span className="text-safety">before they start.</span>
        </h2>
        <div className="mt-10"><PilotButton testId="final-cta-pilot" /></div>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
          <a href={CONTACT_URL} data-testid="final-link-evaluation" className="underline underline-offset-4 hover:text-white">$1,500 compliance evaluation</a>
          <a href={CONTACT_URL} data-testid="final-link-readiness" className="underline underline-offset-4 hover:text-white">{READINESS_LABEL}</a>
        </div>
        <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
          SafeForm is not a substitute for clinical assessment. AI findings are suggestions reviewed by a person.
        </div>
      </div>
    </section>
  );
}
